/**
 * API Error Classes
 *
 * Typed error hierarchy for API responses.
 * These mirror HTTP status codes and allow for specific error handling.
 */

/**
 * Base API error with status code and optional response data
 */
export class ApiError extends Error {
  override readonly name: string = 'ApiError';

  constructor(
    public readonly status: number,
    message: string,
    public readonly data?: unknown
  ) {
    super(message);
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Check if this is a specific error type
   */
  isUnauthorized(): this is UnauthorizedError {
    return this.status === 403;
  }

  isUnauthenticated(): this is UnauthenticatedError {
    return this.status === 401;
  }

  isNotFound(): this is NotFoundError {
    return this.status === 404;
  }

  isValidationError(): this is ValidationError {
    return this.status === 422;
  }
}

/**
 * 401 Unauthenticated - User is not logged in or token is invalid/expired
 */
export class UnauthenticatedError extends ApiError {
  override readonly name = 'UnauthenticatedError';

  constructor(message = 'Authentication required') {
    super(401, message);
    Object.setPrototypeOf(this, UnauthenticatedError.prototype);
  }
}

/**
 * 403 Unauthorized - User is logged in but lacks permission
 */
export class UnauthorizedError extends ApiError {
  override readonly name = 'UnauthorizedError';

  constructor(message = 'Permission denied') {
    super(403, message);
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

/**
 * 404 Not Found - Resource does not exist
 */
export class NotFoundError extends ApiError {
  override readonly name = 'NotFoundError';

  constructor(message = 'Resource not found') {
    super(404, message);
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * 422 Validation Error - Request data is invalid
 */
export class ValidationError extends ApiError {
  override readonly name = 'ValidationError';

  constructor(
    message = 'Validation failed',
    public readonly errors?: Record<string, string[]>
  ) {
    super(422, message, errors);
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * 500+ Server Error - Backend failure
 */
export class ServerError extends ApiError {
  override readonly name = 'ServerError';

  constructor(
    status = 500,
    message = 'Internal server error'
  ) {
    super(status, message);
    Object.setPrototypeOf(this, ServerError.prototype);
  }
}

/**
 * Network Error - Request failed to reach server
 */
export class NetworkError extends ApiError {
  override readonly name = 'NetworkError';

  constructor(message = 'Network error') {
    super(0, message);
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
